import React, { createContext, useCallback } from "react";
import { Constants, Enums } from "../../../utils";
import Localization from "../../../localization";
import AlarmConnection from "./alarmConnection";
import AlarmCentral from "./alarmCentral";
import SMHI from "./smartHomeIntegration";
import useViewModel from "../viewModel";
import Location from "./location";
import AlarmSMS from "./alarmSMS";
import Payment from "./payment";
import Connect from "./connect";
import { Flex } from "antd";
import {
  Container,
  FSLoader,
  Collapse,
  Progress,
  Button,
  Modal,
  Result,
  Text,
  Alert,
  Card,
} from "../../../components";

const itemComponents = {
  [Enums.serviceCardIDs.smhi]: SMHI,
  [Enums.serviceCardIDs.alarmSms]: AlarmSMS,
  [Enums.serviceCardIDs.subscriptionStatus]: Payment,
  [Enums.serviceCardIDs.connect]: Connect,
  [Enums.serviceCardIDs.location]: Location,
  [Enums.serviceCardIDs.alarmConnection]: AlarmConnection,
  [Enums.serviceCardIDs.alarmCentral]: AlarmCentral,
};

const View = () => {
  const {
    data,
    uuid,
    error,
    loading,
    progress,
    refs = {},
    cards = [],
    isConfigured,
    ...props
  } = useViewModel();

  const renderItem = useCallback((item) => {
    const Item = itemComponents[item.id];
    if (!Item) return null;
    if (!!Item.Inactive && !item.activated)
      return (
        <Item.Inactive
          activate={() =>
            item.onBtnClick({ progress: item.id, activated: item.activated })
          }
          readMore={() => window.open(item.readMoreUrl)}
        />
      );
    return (
      <Item
        id={item.id}
        title={item.title}
        onEditClick={item.onBtnClick}
        {...item}
      />
    );
  }, []);

  const renderUnConfiguredItem = useCallback(
    (_, index) => {
      const done = index < progress;
      const disable = index > progress;
      const active =
        (index === cards.length - 1 && !disable) || progress === index;
      return (
        <div id={_.id}>
          <Collapse
            done={done}
            active={active}
            disable={disable}
            panelStyle={"padding: 0 !important"}
            title={`${Localization.general.step} ${index + 1}: ${_.stepTitle}`}
            children={
              _.activated !== undefined && !_.activated ? (
                <Button
                  type="primary"
                  title={Localization.general.get_started}
                  onClick={() =>
                    _.onBtnClick({ progress: _.progress, activated: false })
                  }
                />
              ) : done ? (
                renderItem({
                  ..._,
                  title: "",
                  style: { borderRadius: 0 },
                  styles: { body: { padding: 0 } },
                })
              ) : (
                <Flex gap={"1rem"}>
                  <Button
                    type="primary"
                    title={Localization.general.edit}
                    onClick={() =>
                      _.onBtnClick({ progress: _.progress, activated: false })
                    }
                  />
                  {!!_.optional && (
                    <Button
                      title={Localization.general.later}
                      onClick={() =>
                        props.updateProgress({ [_.progress]: false })
                      }
                    />
                  )}
                </Flex>
              )
            }
          />
        </div>
      );
    },
    [progress, data?.configuration?.progress]
  );

  const renderUnConfiguredState = useCallback(() => {
    const percent = ((progress / cards.length) * 100).toFixed(1);
    return (
      <>
        <Flex vertical gap={"2rem"}>
          <Card>
            <Text
              level={4}
              weight={700}
              children={Localization.service.unconf_title}
            />
            <Text
              color="secondary"
              className="m-15-t"
              children={Localization.service.unconf_desc}
            />
            <Progress className="m-30-t" percent={percent} />
          </Card>
          {React.Children.toArray(cards.map(renderUnConfiguredItem))}
        </Flex>
        {percent >= 100 && (
          <Button
            type="primary"
            className="m-30-t"
            title={Localization.general.done}
            onClick={() => props.updateSubscription()}
          />
        )}
      </>
    );
  }, [progress, cards]);

  if (loading) return <FSLoader />;
  if (!!error)
    return (
      <Result
        {...error}
        title={Localization.not_found.title}
        subTitle={Localization.not_found.desc}
      />
    );
  return (
    <React.Fragment>
      <Container>
        {data?.state === "error" && (
          <Alert
            type="error"
            className="m-20-b"
            message={Localization.service.error_title}
            description={Localization.formatString(
              Localization.service.error_desc,
              Constants.Project.emails.support
            )}
          />
        )}
        {!isConfigured ? (
          renderUnConfiguredState()
        ) : (
          <Flex
            vertical
            gap={"2rem"}
            children={React.Children.toArray(cards.map(renderItem))}
          />
        )}
      </Container>
      <Modal
        ref={refs.editLocationModal}
        child={() => <Location.Homes />}
        title={Localization.service.edit_loc_title}
      />
      <Modal
        ref={refs.editAlarmConnectionModal}
        title={Localization.service.edit_alarm_connection_title}
        child={(data) =>
          data.type === "edit" ? (
            <AlarmConnection.AlarmModel.Edit
              onDone={() => refs.editAlarmConnectionModal.current.close()}
            />
          ) : (
            <AlarmConnection.AlarmModel
              onCancel={() => refs.editAlarmConnectionModal.current.close()}
              onDone={() => refs.editAlarmConnectionModal.current.close()}
            />
          )
        }
      />
      <Modal
        ref={refs.editAlarmOperatorModal}
        child={() => <AlarmCentral.Settings />}
        title={Localization.service.card_3_title}
      />
      <Modal
        ref={refs.editGroupsModal}
        title={Localization.general.connect}
        child={(data) => (
          <Connect.Collaborators
            {...data}
            close={() => refs.editGroupsModal.current.close()}
          />
        )}
      />
      <AlarmSMS.Introduction ref={refs.editAlarmSMSModal} />
      <SMHI.ModifyWebhook ref={refs.editSMHIModal} />
    </React.Fragment>
  );
};

export default View;
